import { BrButton, BrLoading, BrSelect } from '@govbr-ds/react-components';
import { SelectOptions } from '@govbr-ds/react-components/dist/components/BrSelect';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useNotificacao } from 'hooks/useNotificacao';
import { IFatoGerador, IRetornoListagemBaseCalculo, PaginasOutrosRegimes } from 'interfaces/IBasesCalculo';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-dsgov';
import style from './selectBaseCalculo.module.scss';

interface ISelectBaseCalculoProps {
  entrarModoEdicao: (chaveBaseCalculo: PaginasOutrosRegimes) => void;
  baseCalculoCadastradas?: any;
}

const SelectBaseCalculo = ({ entrarModoEdicao, baseCalculoCadastradas }: ISelectBaseCalculoProps) => {
  const SEPARADOR_CHAVE = '|';

  const MSG_SEM_SELECAO = 'Selecione um tipo de base de cálculo para adicionar';
  const MSG_DESABILITADA =
    'Base de cálculo já cadastrada na operação, caso seja necessário incluir outras informações, edite a existente';

  const mostrarNotificacao = useNotificacao();
  const { getFatoGerador, getListaFormacoesBaseCalculo } = useBackEndRequests();

  const [optionsBaseCalculo, setOptionsBaseCalculo] = useState<SelectOptions[]>([]);
  const [baseCalculoSelecionada, setBaseCalculoSelecionada] = useState<string>('');
  const [chaveBaseCalculo, setChaveBaseCalculo] = useState<string>('');

  const [optionsFatoGerador, setOptionsFatoGerador] = useState<SelectOptions[]>([]);
  const [fatoGeradorSelecionado, setFatoGeradorSelecionado] = useState<number>(-1);

  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const {
    data: fatoGeradorData,
    error: fatoGeradorError,
    isFetching: fatoGeradorIsLoading,
    refetch: refetchFatoGerador,
  } = useQuery<IFatoGerador[], AxiosError>({
    queryKey: ['getFatoGerador'],
    queryFn: () => getFatoGerador(),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: guiaRecolhimentoId !== '',
    staleTime: 0,
    refetchOnMount: true,
  });

  const {
    data: formacoesCalculoData,
    error: formacoesCalculoError,
    isFetching: formacoesCalculoIsLoading,
    refetch: refetchFormacoesCalculo,
  } = useQuery<IRetornoListagemBaseCalculo[], AxiosError>({
    queryKey: ['getListaFormacoesBaseCalculo', guiaRecolhimentoId, fatoGeradorSelecionado],
    queryFn: () => getListaFormacoesBaseCalculo(guiaRecolhimentoId, fatoGeradorSelecionado),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: fatoGeradorSelecionado !== -1,
    staleTime: 0,
  });

  useEffect(() => {
    if (formacoesCalculoData) {
      const options = formacoesCalculoData.map((base, index) => ({
        label: base.rotulo,
        value: `${index}${SEPARADOR_CHAVE}${base.valor}`,
      }));

      setOptionsBaseCalculo(options);
    }
  }, [formacoesCalculoData]);

  useEffect(() => {
    if (fatoGeradorData) {
      const options = fatoGeradorData.map((base, index) => ({
        label: base.rotulo,
        value: base.valor,
      }));

      setOptionsFatoGerador(options);
    }
  }, [fatoGeradorData]);

  useEffect(() => {
    if (baseCalculoSelecionada) {
      const chaveBaseCalculo = baseCalculoSelecionada.split(SEPARADOR_CHAVE)[1];

      setChaveBaseCalculo(chaveBaseCalculo);
    }
  }, [baseCalculoSelecionada]);

  useEffect(() => {
    if (fatoGeradorSelecionado !== -1) {
      refetchFormacoesCalculo();
    }
  }, [fatoGeradorSelecionado]);

  useEffect(() => {
    setBaseCalculoSelecionada('');
    setFatoGeradorSelecionado(-1);
  }, [baseCalculoCadastradas]);

  const verificarBaseCalculoDesabilitada = () => {
    const baseCalculo = formacoesCalculoData?.find((base) => base.valor === chaveBaseCalculo);

    if (baseCalculo) {
      return baseCalculo.isHabilitado === false;
    } else {
      return true;
    }
  };

  const validarBaseCalculo = () => {
    if (baseCalculoSelecionada == '') {
      return MSG_SEM_SELECAO;
    }

    if (verificarBaseCalculoDesabilitada()) {
      return MSG_DESABILITADA;
    }

    return '';
  };

  const onClickAdicionar = () => {
    const msgValidacao = validarBaseCalculo();
    if (msgValidacao !== '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: msgValidacao,
      });
    } else {
      entrarModoEdicao(chaveBaseCalculo as PaginasOutrosRegimes);
      window.scrollTo(0, 300);
    }
  };

  return (
    <div className={style.selectBaseCalculo}>
      {fatoGeradorIsLoading && (
        <div className={style.loadingContainer} data-testid='loading-container'>
          <BrLoading large />
        </div>
      )}
      {!fatoGeradorIsLoading && !!fatoGeradorError && (
        <div className={style.errorContainer} data-testid='error-container'>
          <span className={style.errorText}>Não foi possível recuperar a lista de fato geradores</span>
          <BrButton data-testid='btn-refetch' size='small' onClick={() => refetchFatoGerador()}>
            (Tentar novamente)
          </BrButton>
        </div>
      )}
      {!fatoGeradorIsLoading && !fatoGeradorError && (
        <form autoComplete='off'>
          <Row className={style.lista}>
            <Col className={style.fatoGeradorSelect}>
              <BrSelect
                data-testid='select-fato-gerador'
                label='Fato Gerador'
                placeholder='Selecione o fato gerador...'
                options={optionsFatoGerador}
                value={fatoGeradorSelecionado}
                key={fatoGeradorSelecionado}
                onChange={(value: number) => setFatoGeradorSelecionado(value)}
              />
            </Col>
            <Col className={style.formacaoBaseCalculoSelect} auto xl>
              {formacoesCalculoIsLoading && <BrLoading />}
              {!formacoesCalculoIsLoading && !!formacoesCalculoError && (
                <div className={style.errorContainer} data-testid='error-container'>
                  <span className={style.errorText}>Não foi possível recuperar a lista de base de cálculo</span>
                  <BrButton data-testid='btn-refetch' size='small' onClick={() => refetchFatoGerador()}>
                    (Tentar novamente)
                  </BrButton>
                </div>
              )}
              {!formacoesCalculoIsLoading && !formacoesCalculoError && (
                <BrSelect
                  data-testid='select-base-calculo'
                  label='Formação de Base de Cálculo'
                  placeholder='Selecione a formação de base de cálculo...'
                  options={optionsBaseCalculo}
                  value={baseCalculoSelecionada}
                  key={baseCalculoSelecionada}
                  onChange={(value: string) => setBaseCalculoSelecionada(value)}
                  disabled={fatoGeradorSelecionado === -1}
                />
              )}
            </Col>
            <Col className={style.botao} auto>
              <BrButton data-testid='btn-adicionar' secondary onClick={() => onClickAdicionar()} size='small'>
                <i className={`fas fa-plus`} /> Adicionar nova base de cálculo
              </BrButton>
            </Col>
          </Row>
        </form>
      )}
    </div>
  );
};

export default SelectBaseCalculo;
